<template>
  <EnterViewportDetector class="flex justify-center" @enter="inViewport = true">
    <div class="w-full md:max-w-[706px]">
      <RevSkeleton
        v-if="!renderSearch"
        :class="helperText ? 'min-h-80' : 'min-h-48'"
        data-test="trade-in-search-skeleton"
        shape="rectangle"
      />

      <ClientOnly>
        <TradeInSearchBar
          v-if="renderSearch"
          ref="trade-in-search"
          :default-option-label="ctaLink?.label"
          :display-default-option="!!ctaLink"
          :display-select-example="!!helperText"
          :label="label ? label : i18n(translations.searchLabel)"
          :select-example-text="helperText || ''"
          :tracking="blockTracking"
          :zone="blockTracking.zone"
          @default-item-selected="handleDefaultItemSelected"
          @item-selected="handleItemSelected"
        />
      </ClientOnly>
    </div>
  </EnterViewportDetector>
</template>

<script setup lang="ts">
import { navigateTo, useRouter } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { TradeInSearchApiProps } from '@backmarket/http-api/src/api-specs-content/models/trade-in-search'
import TradeInSearchBar from '@backmarket/nuxt-layer-buyback/app/scopes/offer/components/TradeInSearchBar/TradeInSearchBar.vue'
import type { BuybackProduct } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevSkeleton } from '@ds/components/Skeleton'

import type { BlockProps } from '../../models/content-block'
import EnterViewportDetector from '../../shared-components/EnterViewportDetector/EnterViewportDetector.vue'

import translations from './TradeInSearch.translations'

export type TradeInSearchBlockProps = TradeInSearchApiProps & BlockProps

const {
  blockPosition,
  label = undefined,
  tracking,
  ctaLink = undefined,
  helperText = undefined,
} = defineProps<TradeInSearchBlockProps>()

const router = useRouter()
const i18n = useI18n()
const logger = useLogger()

const hydrated = ref<boolean>(false)
const inViewport = ref<boolean>(false)

const blockTracking = {
  ...tracking,
  zone: tracking?.zone?.toString() ?? '',
  name: 'trade_in_search',
}

onMounted(() => {
  hydrated.value = true
})

async function handleDefaultItemSelected() {
  if (!ctaLink?.link?.href) return

  await navigateTo(ctaLink.link.href)
}

async function handleItemSelected(item: BuybackProduct) {
  const query = {
    brand: item.brand,
    model: item.model,
    model_family: item.productLine,
    nextStep: 'false',
  }

  const linkParams = {
    name: 'buyback_device_form',
    type: 'internal',
    params: { slug: item.category, id: item.category },
    query: removeEmptyValuesInObject(query),
  }

  const link = toNuxtLink(linkParams)

  try {
    if (router.resolve(link).matched.length > 0) {
      await navigateTo(link)
    }
  } catch {
    logger.error('[TradeInSearch] failed to resolve link on option select', {
      info: { item, linkParams, link, zone: blockTracking.zone },
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  }
}

// blockPosition triggers faster than inViewport on page load, but inViewport
// is needed if the block is not at the top of the page
const renderSearch = computed(() => {
  if (!hydrated.value) return false
  if (!blockPosition) return inViewport.value

  return inViewport.value || blockPosition <= 2
})
</script>
